import { memo } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import { Field, FieldArray, withFormik } from 'formik';
import * as Yup from 'yup';
import { IdentityFields } from './identity.form';
import { TextField } from '../../../../components/formik/text-field';
import {
    isValidNational,
    isValidNumbers,
    isValidPersianAlpha,
} from 'utils/custom-validation';

type Signature = {
    name: string;
    label: string;
    icon: any;
    options?: Array<any>;
};

const SignatureFields: Array<Signature> = IdentityFields;

const SignatureSchema = Yup.object().shape({
    name: Yup.string()
        .required('فیلد نام الزامی است')
        .min(3, 'فیلد نام باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام باید حداکثر 64 کاراکتر باشد')
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است'),

    family: Yup.string()
        .required('فیلد نام خانوادگی الزامی است')
        .min(3, 'فیلد نام خانوادگی باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام خانوادگی باید حداکثر 64 کاراکتر باشد')
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است'),

    father: Yup.string()
        .required('فیلد نام پدر الزامی است')
        .min(3, 'فیلد نام پدر باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام پدر باید حداکثر 64 کاراکتر باشد')
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است'),

    passport: Yup.string()
        .required('وارد کردن شماره شناسنامه الزامی است')
        .min(3, 'فیلد شماره شناسنامه باید حداقل 3 کاراکتر باشد')
        .max(10, 'فیلد شماره شناسنامه باید حداکثر 10 کاراکتر باشد')
        .matches(
            isValidNumbers,
            'فیلد شماره شناسنامه باید فقط شامل اعداد باشد',
        ),

    national: Yup.string()
        .required('وارد کردن کد ملی الزامی است')
        .length(10, 'فیلد کد ملی باید دقیقاً 10 کاراکتر باشد')
        .test('is-valid-national-code', 'کد ملی وارد شده معتبر نیست', (value) =>
            isValidNational(value),
        ),
});

const RenderSignatureFields = ({
    signatureIndex,
    onDelete,
    disableAutoFill = false,
}: any): JSX.Element => {
    return (
        <Box
            sx={{
                backgroundColor: 'background.default',
                mb: 2,
                p: 2,
                borderRadius: 2,
                position: 'relative',
            }}>
            <Grid container spacing={2}>
                {SignatureFields.map((field) => (
                    <Grid
                        item
                        xs={12}
                        md={6}
                        key={`${signatureIndex}.${field.name}`}>
                        <Field
                            {...field}
                            name={`signatures.${signatureIndex}.${field.name}`}
                            component={TextField}
                            disableAutoFill={disableAutoFill}
                        />
                    </Grid>
                ))}
            </Grid>
            <IconButton
                sx={{ position: 'absolute', left: '-41px', bottom: '-2px' }}
                color="error"
                size="small"
                onClick={() => onDelete(signatureIndex)}>
                <i className="fa-light fa-trash"></i>
            </IconButton>
        </Box>
    );
};

const SignatureFormTemplate = memo((props: any): JSX.Element => {
    // const [signatures, updateSignatures] = useState<Array<any>>([]);

    // useEffect(() => {
    // if (signatures.length < 1) updateSignatures(props?.signatures || []);
    // }, [props?.signatures]);

    return (
        <>
            <Box sx={{ pl: 4 }}>
                <FieldArray
                    name="signatures"
                    render={(arrayHelpers) => {
                        const signatures =
                            arrayHelpers.form.values.signatures || [];
                        const el = signatures?.map(
                            (signature: any, index: number) => {
                                return (
                                    <RenderSignatureFields
                                        key={index}
                                        onDelete={(signatureIndex: number) => {
                                            arrayHelpers.remove(signatureIndex);
                                        }}
                                        signatureIndex={index}
                                        disableAutoFill={props.disableAutoFill}
                                    />
                                );
                            },
                        );

                        return (
                            <>
                                {el}
                                <IconButton
                                    color="success"
                                    onClick={() => {
                                        arrayHelpers.push({
                                            name: '',
                                            family: '',
                                            father: '',
                                            passport: '',
                                            national: '',
                                        });
                                    }}>
                                    <i className="fa-light fa-circle-plus"></i>
                                </IconButton>
                            </>
                        );
                    }}
                />
            </Box>
        </>
    );
});

const SignaturForm = withFormik({
    mapPropsToValues: (props: any) => {
        return props.signatures;
    },
    validationSchema: SignatureSchema,
    validateOnChange: false,
    validateOnBlur: true,
    handleSubmit: (values, { setSubmitting }) => {},
})(SignatureFormTemplate);

export {
    SignaturForm,
    SignatureFormTemplate,
    SignatureSchema,
    SignatureFields,
};
